import React, { useEffect, useState } from "react";
import { api, type KeepTermsView } from "../api.js";
import { addKeepTerm, removeKeepTerm } from "../languages/keepTerms.js";

/**
 * "Kept exactly as written": the product's name and the names the script
 * mentions, never translated in the voice or on screen. Found from the script
 * until the customer changes the list; every change is saved at once.
 */
export const KeepTerms: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [view, setView] = useState<KeepTermsView | null>(null);
  const [adding, setAdding] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    api
      .keepTerms(projectId)
      .then((next) => {
        if (current) setView(next);
      })
      .catch(() => {
        if (current) setError("The words kept as written couldn't be loaded.");
      });
    return () => {
      current = false;
    };
  }, [projectId]);

  const save = async (terms: string[] | null) => {
    setError(null);
    try {
      setView(await api.saveKeepTerms(projectId, terms));
    } catch {
      setError("That change couldn't be saved. Try again.");
    }
  };

  if (!view) return error ? <p className="text-[12px] text-signal-bad">{error}</p> : null;

  return (
    <section aria-labelledby="keep-terms" className="flex flex-col gap-2 rounded-[10px] bg-bay-850 p-3">
      <h3 id="keep-terms" className="text-[12px] font-semibold text-ink-300">
        Kept exactly as written
      </h3>
      <p className="text-[12px] leading-[1.45] text-ink-300">
        Never translated, in the voice or on screen.{" "}
        {view.custom ? "Your list." : "Found in your script; add any we missed."}
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {view.terms.map((term) => {
          const product = term.toLowerCase() === view.productName.toLowerCase();
          return (
            <li
              key={term}
              className="inline-flex items-center gap-1 rounded-full border border-[var(--edge)] bg-white py-1 pr-1 pl-2.5 text-[12.5px] text-ink-100"
            >
              {term}
              {product ? null : (
                <button
                  type="button"
                  className="rounded-full px-1 text-ink-300 hover:text-ink-100 focus-visible:outline-2 focus-visible:outline-[var(--edge-focus)]"
                  aria-label={`Translate ${term}`}
                  onClick={() => void save(removeKeepTerm(view.terms, term))}
                >
                  ×
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <form
        className="flex gap-1.5"
        onSubmit={(event) => {
          event.preventDefault();
          const next = addKeepTerm(view.terms, adding);
          if (next) void save(next);
          setAdding("");
        }}
      >
        <label htmlFor="keep-term-input" className="sr-only">
          A word to keep as written
        </label>
        <input
          id="keep-term-input"
          className="input h-8 flex-1 text-[13px]"
          placeholder="Add a name, e.g. a feature"
          maxLength={60}
          value={adding}
          onChange={(event) => setAdding(event.target.value)}
        />
        <button type="submit" className="btn btn-secondary btn-sm" disabled={!adding.trim()}>
          Add
        </button>
      </form>
      {view.custom ? (
        <button
          type="button"
          className="self-start text-[12px] text-ink-300 underline underline-offset-2 hover:text-ink-100"
          onClick={() => void save(null)}
        >
          Go back to the words found in the script
        </button>
      ) : null}
      {error ? <p className="text-[12px] text-signal-bad">{error}</p> : null}
    </section>
  );
};
